import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import {
  CreateLoanParamsBridgeLoan,
  DialogTitle,
  LoanStage
} from "@toorak/tc-common-fe-sdk";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Dialog,
  DialogContent,
  Grid,
  Checkbox,
  TextField,
  Theme
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../stores/rootReducer";
import {
  getLoanHistory,
  postLoan,
  updateSummaryDetails
} from "../create-loan.action";
import { getLoanType } from "../../config/config";
import { getBridgeLoanGuarantorInformationToPass } from "../CreateLoanHeader";
import { updatePollingData } from "../../stores/SettlementAndPurchase/SettlementAndPurchase.reducer";
import { postLoanSuccess } from "../create-loan.reducer";
import { moveToReviewInProgress } from "../../utils/updateStatus";
import { getCookie } from "../../utils/cookies";
import { WaiversCompensatingFactors } from "../exceptions-and-enquiries/WaiversCompensatingFactors";
import WaiveTestsCard from "./WaiveTestsCard";
import { bulkWaiverApproval } from "../../stores/WaiverRequest/WaiverRequest.action";
import { hideLoader, showLoader } from "../../stores/loaderAndException/loaderAndException.action";
import { globalGetDataByLoanID } from "../CreateLoan";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogTitle: {
      color: "#32325D",
      fontFamily: "Open Sans",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "normal",
      padding: "16px 14px"
    },
    commentField: {
      color: "#32325D",
      fontFamily: "Open Sans",
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "18px" /* 150% */
    },
    requiredField: {
      color: "#F75676",
      fontFamily: "Open Sans",
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "18px"
    },
    acceptButton: {
      borderRadius: "4px",
      background: "#059F5D",
      boxShadow: "0px 2px 4px 0px rgba(50, 50, 93, 0.20)",
      display: "flex",
      padding: "8px 20px",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      color: "#FFF",
      fontFamily: "Open Sans",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "normal"
    },
    rejectButton: {
      display: "flex",
      padding: "8px 20px",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      borderRadius: "4px",
      background: "#F75676",
      boxShadow: "0px 2px 4px 0px rgba(50, 50, 93, 0.20)",
      color: "#FFF",
      fontFamily: "Open Sans",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "normal"
    },
    cancelButton: {
      display: "flex",
      padding: "8px 20px",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      borderRadius: "4px",
      border: "1px solid #E2E4F1",
      background: "#FFF",
      boxShadow: "0px 2px 4px 0px rgba(50, 50, 93, 0.20)",
      color: "#32325D",
      fontFamily: "Open Sans",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "normal"
    },
    waiveButton: {
      borderRadius: "4px",
      background: "#5e72e4",
      boxShadow: "0px 2px 4px 0px rgba(50, 50, 93, 0.20)",
      display: "flex",
      padding: "8px 20px",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      color: "#FFF",
      fontFamily: "Open Sans",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "normal"
    },
    failedContainer: {
      borderRadius: "6px",
      backgroundColor: "#f75676",
      padding: "2px 5px",
      display: "flex",
      justifyContent: "space-around",
      alignItems: "center",
      maxWidth: "75px",
      height: "20px"
    },
    warningIcon: {
      //   padding: "2px",
      height: "19px"
    },
    failedTypography: {
      fontSize: "10px",
      fontWeight: "bold",
      color: "#ffffff",
      height: "14px",
      marginLeft: "4px"
    },
  })
);

const EnhanceCreditReviewDialog = (props: any) => {
  const { enhanceCreditReview, setEnhanceCreditReview, waiveTests, setWaiveTests } = props;
  const [callAutoSave, setCallAutoSave] = useState<{
    value: boolean;
    commentList: any[];
  }>({ value: false, commentList: [] });
  const [comment, setComment] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [riskBucketApproved, setRiskBucketApproved] = useState<boolean>(true);
  const [selectedCompensatingFactors, setselectedCompensatingFactors] = useState([]);
  const [failedTestsData, setFailedTestsData] = useState([]);

  const classes = useStyles();
  const dispatch = useDispatch();

  const { loanDetails } = useSelector<RootState, any>(
    (state) => state.createLoanStore
  );
  const { loanEvaluationResult, representableEvaluationResultsData, waiversCreated } = useSelector<RootState, any>(
    (state) => state.evaluationResultsStore
  );
  const {
    bridgeLoanBorrowerInformation,
    bridgeLoanInformation,
    bridgeLoanEconomics,
    thirtyYearLoanEconomics,
    thirtyYearLoanInformation,
    bridgeLoanGuarantorInformation
  } = useSelector<RootState, any>((state) => state.createLoanStore.loanDetails);

  const { takeoutPartnerId } = thirtyYearLoanInformation?.payload ?? {}

  const customerDetails = useSelector<RootState, any>(
      (state) => state.evaluationResultsStore.customerInfo
    );

  const { loanId, loanType, loanStage } = useParams<{
    loanId: string;
    loanStage: any;
    loanType: string;
  }>() as any;
  const bridgeLoanGuarantorInformationToPass =
    getBridgeLoanGuarantorInformationToPass(bridgeLoanGuarantorInformation);

  const disableWaiveTests = useMemo(() => {
    const selectedTests = failedTestsData?.filter((test: any) => test?.isChecked)
    return !(selectedTests?.length && ((selectedCompensatingFactors?.length > 1 && hasRequiredCompensatingFactors(selectedTests)) || hasRequiredCompensatingFactors(selectedTests)) && comment)

  },[failedTestsData, comment, selectedCompensatingFactors])

  const disableRejectBtn = useMemo(() => {
    if(failedTestsData?.length === 0 || failedTestsData?.filter((test: any) => test?.isChecked)?.length === 0) 
      return comment?.length <= 0
    // const requestedWaiverTests = failedTestsData?.filter((test: any) => test?.waiverData && test?.isChecked)
    // return !(comment?.length && requestedWaiverTests?.length && (selectedCompensatingFactors?.length > 1 || hasRequiredCompensatingFactors(requestedWaiverTests))) 
    return !(comment?.length > 0 && riskBucketApproved && !disableWaiveTests)
  },[failedTestsData, comment, selectedCompensatingFactors])

  const disableApproveBtn = useMemo(() => {
    if(failedTestsData?.length === 0 || failedTestsData?.filter((test: any) => test?.isChecked)?.length === 0) 
      return !(comment?.length > 0 && riskBucketApproved)
    return !(comment?.length > 0 && riskBucketApproved && !disableWaiveTests)
  },[failedTestsData, comment, riskBucketApproved])

  function hasRequiredCompensatingFactors(data: any) {
    return data.every((item: any) => 
      Array.isArray(item.compensatingFactors) && item.compensatingFactors.length >= 2
    );
  }


  const handleCloseModal = () => {
    setEnhanceCreditReview(false);
    setWaiveTests(false)
    setRiskBucketApproved(true);
  };

  const convertBooleanToString = (value: boolean) => {
    if (value) {
      return "Yes";
      // eslint-disable-next-line no-else-return
    } else {
      return "No";
    }
  };

  const isRiskBucketApprovalRequired = useMemo(() => {
    const riskBucket =
      loanEvaluationResult?.loanResult?.[loanId]?.riskScores?.riskBucket;
    if (riskBucket) {
      return riskBucket === "4" || riskBucket === "5";
    }
    return false;
  }, [loanEvaluationResult, loanId]);

  const handleEnhancedCreditReviewChange = (value: string) => {
    const personId = getCookie("person_id");

    if (!comment) return;
    const changedValue = value;
    const commentList: any = [
      {
        value: changedValue,
        comment,
        updatedBy: personId,
        field: `data.loan.loanDetailId.loanSummaryId.enhancedReview`
      },
    ];
    if (isRiskBucketApprovalRequired) {
      commentList.push({
        value: true,
        comment: "Approved from Summary checklist",
        updatedBy: personId,
        field: `data.loan.loanDetailId.loanSummaryId.riskBucketApproved`
      });
    }
    dispatch(updateSummaryDetails("enhancedReview", changedValue, true));
    if (isRiskBucketApprovalRequired) {
      dispatch(
        updateSummaryDetails("riskBucketApproved", riskBucketApproved, true)
      );
    }
    setCallAutoSave({ value: true, commentList });
    if(failedTestsData?.length) {
      handleWaiveMultipleFailTests(changedValue === "No" ? "REJECTED" : "APPROVED")
    }
    setIsLoading(true);
  };

  const saveCheckListValue = (commentList: any[]) => {
    let loanEconomics: any;
    let loanInformation: any;
    let isDSCR: boolean;
    if (loanType === getLoanType[0].displayValue) {
      isDSCR = false;
      loanEconomics = { ...bridgeLoanEconomics.payload };
      loanInformation = { ...bridgeLoanInformation.payload };
    } else {
      isDSCR = true;
      loanEconomics = { ...thirtyYearLoanEconomics.payload };
      loanInformation = { ...thirtyYearLoanInformation.payload };
    }

    postLoan(
      {
        loanId,
        loanType,
        borrowerInformation: bridgeLoanBorrowerInformation,
        guarantorInfo: bridgeLoanGuarantorInformationToPass,
        ...loanInformation,
        ...loanEconomics,
        loanConfig: loanDetails.loanConfig,
        loanSummary: loanDetails.loanSummary
      },
      false,
      true,
      false,
      "",
      commentList
    )
      .then((response: CreateLoanParamsBridgeLoan) => {
        if (loanStage === LoanStage.post) {
          // dispatch({
          //   type: UPDATE_POLLING_DATA,
          //   payload: {
          //     startPolling: true,
          //     timeInMs: 0
          //   }
          // });
          dispatch(
            updatePollingData({
              startPolling: true,
              timeInMs: 0
            })
          );
        }
        // dispatch({
        //   type: POST_LOAN_SUCCESS,
        //   payload: {
        //     reservation: response
        //   }
        // });
        dispatch(
          postLoanSuccess({
            reservation: response
          })
        );
        moveToReviewInProgress(
          loanId,
          loanStage,
          isDSCR,
          loanDetails,
          loanEvaluationResult,
          dispatch,
          "Updated from Summary tab by the system as prerequisites were met.",
          {
            loanSummary: loanDetails.loanSummary
          }
        );
        dispatch(getLoanHistory(`${loanId}_${loanStage}`));
        setEnhanceCreditReview(false);
        setWaiveTests(false)
        setRiskBucketApproved(true);
        setComment("");
      })
      .catch((err: any) => console.error(err))
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (callAutoSave.value) {
      saveCheckListValue(callAutoSave.commentList);
      setCallAutoSave({ value: false, commentList: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callAutoSave]);

  useEffect(() => {
    if(representableEvaluationResultsData?.length) {
      const failing = getAllFailingTests(representableEvaluationResultsData)
      setFailedTestsData(failing ?? [])
    }
  },[representableEvaluationResultsData])

  function getAllFailingTests(data: any) {
    const failedData = data.flatMap((item: any) => (item?.failingTests) || []);
    return failedData?.filter((item:any) => item?.waiverData?.waiverInfo?.finalResponse  !== "APPROVED" && !["finalCalculation", "masterTest"].includes(item?.testGroup))
  }

function prepareWaiversData(data: any, responseType: string) {
  const personId = getCookie("person_id")
  const defaultMitigates = [{
    description: "",
    waiverMitigateId: 1
  }]
    return data?.filter((e: any) => responseType === "REJECTED" ? e?.waiverData?.waiverId : true).map((item: any) => {
        const w = item.waiverData || {};
        const waiverMitigates = (w.waiverMitigates || []).map((m: any) => ({
                description: m.description,
                waiverMitigateId: m.waiverMitigateId
            }))
        return {
            ...(w?.waiverId && {waiverId: w?.waiverId}),
            waiverType: w.waiverType ?? "EvaluationResult",
            waiverKeys: w.waiverKeys ?? item?.waiverKeys ?? [],
            title: w.title || item.representableTestName || "",
            description: w.description || item.reasonForFailure || "",
            waiverMitigates: waiverMitigates?.length ? waiverMitigates : defaultMitigates,
            waiverInfo: {
                hasDocuments: false,
                hasDiscussions: false,
                finalResponse: "",
                isInternal: false
            },
            takeoutPartnerId: w.takeoutPartnerId ?? takeoutPartnerId,
            waiverResponses: [
              {
                responseType: responseType ?? "APPROVED",
                responseBy: personId,
                responseDate: new Date().toISOString(),
                isApproved: responseType === "APPROVED",
                compensatingFactors: responseType === "APPROVED" ? item?.compensatingFactors || [] : []
              }
            ]
        }
    });
}



  const handleWaiveMultipleFailTests = async (responseType: string) => {
    try {
      const selectedFailedTests = failedTestsData?.filter((test: any) => test?.isChecked)
      if(selectedFailedTests?.length) {
        dispatch(showLoader());
        const waiversData = prepareWaiversData(selectedFailedTests, responseType)
        console.log("waiversData", {waiversData})
        const reqBody = {
          "identifierName": "LOAN",
          "identifierValue": loanId,
          "secondaryIdentifierName": "LOAN_STAGE",
          "secondaryIdentifierValue": loanStage,
          "comment": comment,
          "takeoutPartner": takeoutPartnerId ?? null,
          responseType: responseType ?? "APPROVED",
          waivers: waiversData,
          loanType: loanType === "BridgeLoan" ? "bridge": "30year" 
        }
        const response: any = await bulkWaiverApproval(reqBody);
        console.log("response", {response})
        if(response?.status === 200)
        await globalGetDataByLoanID(loanId, loanStage, false)
        dispatch(hideLoader());
        handleCloseModal()
      }
    } catch (error) {
      console.error(error)
       dispatch(hideLoader());
    }
    

  }

  const onCompensatingFactorClose = (val: any) => {
    setselectedCompensatingFactors(val);
    setFailedTestsData((previous: any) => {
      return previous?.map((p: any) => ({
          ...p,
          compensatingFactors: p?.isChecked ?  val : p?.compensatingFactors ?? []
        })
      )
    })
  };

  return (
    <Dialog
      open={enhanceCreditReview}
      onClose={() => handleCloseModal()}
      aria-labelledby="enhance-credit-review-title"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: "800px",   // <-- this overrides the MUI default (600px)
        }
      }}
    >
      <DialogTitle
        id="hold-dialog-title"
        onClose={() => handleCloseModal()}
        className={classes.dialogTitle}
      >
        {waiveTests ? "Waive Tests" : "Enhance Credit Review Approval"}
      </DialogTitle>
      <DialogContent sx={{padding: "20px 16px"}}>
        {failedTestsData?.length ? (
          <Grid container spacing={2}>
          <div style={{ marginLeft: "16px", marginTop: "16px", width: "100%"}}>
            <WaiversCompensatingFactors
              setselectedCompensatingFactors={setselectedCompensatingFactors}
              selectedCompensatingFactors={selectedCompensatingFactors}
              onCompensatingFactorClose={onCompensatingFactorClose}
              title={`Compensating Factors (Apply for selected test fails)`}
              fullWidth={true}
            />
          </div>
        </Grid>
        ): null }

        <Grid container mb={1}>
          {failedTestsData?.map((item: any) => {
            return <Grid item key={item?.testName} xs={12} my={1}>
              <WaiveTestsCard  
                data={item} 
                customerDetails={customerDetails} 
                classes={classes}
                setFailedTestsData={setFailedTestsData}
                commonCompensatingFactors= {selectedCompensatingFactors}
              />
          </Grid>
          })}
          
        </Grid>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <span className={classes.commentField}>
              Comments <sup className={classes.requiredField}>*</sup>
            </span>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Type Here"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Grid>
        </Grid>
        {isRiskBucketApprovalRequired && (
          <Grid container spacing={2} style={{ marginTop: "12px" }}>
            <Grid item xs={12}>
              <Checkbox
                checked={riskBucketApproved}
                onChange={(e: any) => setRiskBucketApproved(e.target.checked)}
              />
              <label>Approving loan with 4/5 Risk bucket</label>
            </Grid>
          </Grid>
        )}
        <Grid container spacing={2} style={{ marginTop: "12px" }}>
          {waiveTests ? (
            <Grid item xs={3}>
            <LoadingButton
              className={classes.waiveButton}
              fullWidth
              onClick={() => handleWaiveMultipleFailTests("APPROVED")}
              loading={isLoading}
              variant="contained"
              disabled={disableWaiveTests}
            >
              Waive
            </LoadingButton>
          </Grid>
          ) : (
            <>
            <Grid item xs={3}>
            <LoadingButton
              className={classes.acceptButton}
              fullWidth
              onClick={() => handleEnhancedCreditReviewChange("Yes")}
              loading={isLoading}
              variant="contained"
              // disabled={!(comment?.length > 0 && riskBucketApproved && !disableWaiveTests)}
              disabled={disableApproveBtn}
            >
              Approve
            </LoadingButton>
          </Grid>
          <Grid item xs={3}>
            <LoadingButton
              fullWidth
              className={classes.rejectButton}
              onClick={() => handleEnhancedCreditReviewChange("No")}
              loading={isLoading}
              variant="contained"
              // disabled={comment?.length <= 0 && disableWaiveTests}
              disabled={disableRejectBtn}
            >
              Reject
            </LoadingButton>
          </Grid>
            </>
          )}
          <Grid item xs={3}>
            <LoadingButton
              fullWidth
              className={classes.cancelButton}
              onClick={handleCloseModal}
              loading={isLoading}
              variant="contained"
              // disabled={comment?.length <= 0}
            >
              Cancel
            </LoadingButton>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default EnhanceCreditReviewDialog;
